import { useEffect, useState } from "react";
import UrisCaseFiles from "./UrisCaseFiles";
import UrisSurgicalGuideOrder, {
  DEFAULT_URIS_ENTITLEMENTS,
  type UrisOrderEntitlements,
} from "./UrisSurgicalGuideOrder";
import api from "../../utils/api";
import { useAppSelector } from "../../store/hooks";
import type { CreatedPortalCase } from "./caseDetailsFormNew";

/** Uris clients and the Uris side admin stay on this order form, then upload files here. */
const UrisCaseUpload = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [created, setCreated] = useState<CreatedPortalCase | null>(null);
  const [tokenInfo, setTokenInfo] = useState<{
    mode: "client" | "side_admin";
    count: number;
  } | null>(null);
  const [entitlements, setEntitlements] = useState<UrisOrderEntitlements>(
    DEFAULT_URIS_ENTITLEMENTS,
  );
  const isClient = user?.role === "User";
  const isSideAdmin = user?.role === "Side Admin";

  useEffect(() => {
    if (!isClient && !isSideAdmin) return;
    api
      .get("/api/uris-tokens/me")
      .then((res) => {
        if (!res.data?.data?.applies) return;
        const mode =
          res.data.data.mode === "side_admin" ? "side_admin" : "client";
        setTokenInfo({
          mode,
          count: Number(res.data.data.tokenCount ?? res.data.data.balance) || 0,
        });
        const next = res.data.data.entitlements;
        if (next && typeof next === "object") {
          setEntitlements({
            surgicalTimingGuide: Boolean(next.surgicalTimingGuide),
            addImplants: Boolean(next.addImplants),
            customAbutment: Boolean(next.customAbutment),
            printedModel: Boolean(next.printedModel),
            packageId: next.packageId ?? null,
            packageName: next.packageName ?? null,
          });
        } else if (mode === "side_admin") {
          setEntitlements(DEFAULT_URIS_ENTITLEMENTS);
        }
      })
      .catch(() => setTokenInfo(null));
  }, [isClient, isSideAdmin, created?.case_id]);

  return (
    <div className="flex flex-col gap-4">
      {tokenInfo?.mode === "side_admin" ? (
        <div className="rounded-[14px] border border-[rgba(191,30,46,0.6)] bg-[#221c20] px-5 py-3.5 text-[13.5px] text-[#bdbbbc]">
          Token count: <b className="text-white">{tokenInfo.count}</b>
          <span className="text-[#9C9A9B]"> · Unlimited</span>. Each new case
          adds +1.
        </div>
      ) : null}
      {tokenInfo?.mode === "client" ? (
        <div
          className={`rounded-[14px] border px-5 py-3.5 text-[13.5px] ${
            tokenInfo.count > 0
              ? "border-[rgba(191,30,46,0.6)] bg-[#221c20] text-[#bdbbbc]"
              : "border-[rgba(191,30,46,0.6)] bg-[rgba(191,30,46,0.16)] text-[#F3A0A6]"
          }`}
        >
          Balance tokens: <b className="text-white">{tokenInfo.count}</b>. Each
          new case uses 1 token.
        </div>
      ) : null}
      {!created ? (
        <UrisSurgicalGuideOrder
          onCaseCreated={setCreated}
          entitlements={entitlements}
        />
      ) : (
        <UrisCaseFiles
          caseId={created.case_id}
          caseOwner={created.case_owner}
          caseFolderId={created.box_folder_id}
        />
      )}
    </div>
  );
};

export default UrisCaseUpload;
